import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { ArrowLeft, Radio, Copy, Check, Wifi, WifiOff, Battery, MapPin, Trash2, Mail } from "lucide-react";

export default function PetCollar() {
  const { petId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [brand, setBrand] = useState("generic");
  const [deviceId, setDeviceId] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [msg, setMsg] = useState("");
  const [waitEmail, setWaitEmail] = useState(user?.email || "");
  const [waitDone, setWaitDone] = useState({});

  const load = React.useCallback(() => axios.get(`${API}/pets/${petId}/collar`).then(r => {
    setData(r.data);
    if (r.data.config?.brand) { setBrand(r.data.config.brand); setDeviceId(r.data.config.device_id || ""); setNotes(r.data.config.notes || ""); }
  }).catch(e => setMsg(e?.response?.data?.detail || "Could not load")), [petId]);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true); setMsg("");
    try {
      await axios.put(`${API}/pets/${petId}/collar`, { brand, device_id: deviceId, notes });
      setMsg("Collar connected ✅");
      load();
      setTimeout(() => setMsg(""), 3500);
    } catch (e) { setMsg(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const disconnect = async () => {
    if (!confirm("Disconnect this collar?")) return;
    await axios.delete(`${API}/pets/${petId}/collar`);
    setBrand("generic"); setDeviceId(""); setNotes("");
    load();
  };

  const copy = (text) => { try { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {} };

  const joinWaitlist = async (b) => {
    try { await axios.post(`${API}/collar/waitlist`, { brand: b, email: waitEmail, pet_id: petId }); setWaitDone(prev => ({ ...prev, [b]: true })); }
    catch (e) { setMsg(e?.response?.data?.detail || "Could not join"); }
  };

  if (!data) return <div className="text-ink-soft">Loading…</div>;
  const cfg = data.config;
  const live = !!cfg?.enabled;
  const lastPing = cfg?.last_ping_at;
  const battery = cfg?.battery_pct;

  return (
    <div className="space-y-6" data-testid="collar-page">
      <Link to="/app/pets" className="text-sm font-bold text-brand inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4"/> Back to my pets</Link>

      <header className="bg-surface border border-line rounded-3xl p-5 shadow-soft flex items-center gap-4 flex-wrap">
        <div className={`w-14 h-14 rounded-2xl grid place-items-center ${live ? "bg-leaf/40" : "bg-line"}`}>
          {live ? <Wifi className="w-6 h-6 text-leaf"/> : <WifiOff className="w-6 h-6 text-ink-soft"/>}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-2xl font-extrabold">{data.pet_name}'s GPS collar</h1>
          <div className="text-sm text-ink-soft">{live ? `Connected via ${cfg.brand} · device ${cfg.device_id}` : "No collar connected yet"}</div>
        </div>
        {live && (
          <div className="flex items-center gap-3">
            {typeof battery === "number" && <span className="bg-sunshine/40 rounded-full px-3 py-1 text-xs font-bold inline-flex items-center gap-1"><Battery className="w-3 h-3"/> {battery}%</span>}
            <button onClick={disconnect} data-testid="collar-disconnect" className="text-xs font-bold text-danger inline-flex items-center gap-1 hover:underline"><Trash2 className="w-3 h-3"/> Disconnect</button>
          </div>
        )}
      </header>

      <section className="bg-surface border border-line rounded-3xl p-6 shadow-soft">
        <h2 className="font-heading text-lg font-bold mb-3 flex items-center gap-2"><Radio className="w-5 h-5 text-brand"/> Connect a collar</h2>

        <form onSubmit={save} className="space-y-4">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">Brand</span>
            <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2">
              {data.brands.map(b => (
                <button
                  key={b.id} type="button" data-testid={`brand-${b.id}`}
                  onClick={() => setBrand(b.id)}
                  className={`text-left rounded-2xl p-3 border-2 transition-all ${brand === b.id ? "border-brand bg-brand/5" : "border-line bg-surface hover:border-ink/20"} ${!b.supported && "opacity-70"}`}
                >
                  <div className="font-bold text-sm">{b.label}</div>
                  <div className={`text-[10px] font-bold uppercase tracking-wider mt-1 ${b.supported ? "text-leaf" : "text-ink-soft"}`}>
                    {b.supported ? "✓ Available" : "⏳ Waitlist"}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {SUPPORTED_BRANDS_OK.includes(brand) ? (
            <>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">Device ID / serial number</span>
                <input data-testid="collar-device-id" required value={deviceId} onChange={e => setDeviceId(e.target.value)} placeholder="e.g. PB-ABC-123-456" className="mt-1.5 w-full bg-surface-muted border border-line rounded-2xl px-3 py-2.5 font-mono font-bold text-sm outline-none focus:border-brand"/>
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">Notes (optional)</span>
                <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Red collar with GPS, bought from Amazon" className="mt-1.5 w-full bg-surface-muted border border-line rounded-2xl px-3 py-2.5 font-bold text-sm outline-none focus:border-brand"/>
              </label>
              {msg && <div className="text-sm font-bold text-leaf" data-testid="collar-msg">{msg}</div>}
              <button type="submit" disabled={saving} data-testid="collar-save" className="bg-brand text-white rounded-full px-6 py-3 font-bold disabled:opacity-50 hover:bg-brand-hover">{saving ? "Connecting…" : (cfg ? "Update collar" : "Connect collar")}</button>
            </>
          ) : (
            <WaitlistCTA brand={brand} brandLabel={data.brands.find(b => b.id === brand)?.label} email={waitEmail} setEmail={setWaitEmail} done={waitDone[brand]} onJoin={() => joinWaitlist(brand)}/>
          )}
        </form>
      </section>

      {live && data.webhook_url && (
        <section className="bg-surface border border-line rounded-3xl p-6 shadow-soft" data-testid="webhook-section">
          <h2 className="font-heading text-lg font-bold mb-2 inline-flex items-center gap-2">📡 Your collar's webhook URL</h2>
          <p className="text-sm text-ink-soft mb-3">Configure your GPS device to POST coordinates to this URL. PetBond will plot them live on the tracking map.</p>
          <div className="flex items-center gap-2 bg-ink text-white rounded-2xl p-3">
            <code className="flex-1 text-xs font-mono truncate" data-testid="webhook-url">{data.webhook_url}</code>
            <button onClick={() => copy(data.webhook_url)} className="bg-white/10 hover:bg-white/20 rounded-full px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1" data-testid="webhook-copy">
              {copied ? <><Check className="w-3 h-3"/> Copied</> : <><Copy className="w-3 h-3"/> Copy</>}
            </button>
          </div>

          <details className="mt-4 bg-surface-muted rounded-2xl p-4">
            <summary className="cursor-pointer font-bold text-sm">📝 Show example payload (for your device)</summary>
            <pre className="mt-3 bg-ink text-leaf rounded-xl p-3 text-[11px] overflow-x-auto"><code>{`curl -X POST "${data.webhook_url}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "lat": 12.9716,
    "lng": 77.5946,
    "accuracy": 8,
    "battery_pct": 87,
    "recorded_at": "2026-05-27T11:30:00Z"
  }'`}</code></pre>
            <div className="mt-3 text-xs text-ink-soft">Push as often as the device allows (every 30s – 5min recommended). Keep this URL secret — anyone with it can post location data for {data.pet_name}.</div>
          </details>

          {lastPing && (
            <div className="mt-4 flex items-center gap-2 text-sm" data-testid="collar-last-ping">
              <MapPin className="w-4 h-4 text-brand-green"/>
              <span className="font-bold">Last ping:</span>
              <span className="text-ink-soft">{new Date(lastPing).toLocaleString()}</span>
            </div>
          )}
        </section>
      )}

      <section className="bg-surface-muted rounded-2xl p-4 text-xs text-ink-soft">
        <strong>How does this work?</strong> Most modern GPS pet collars (or even a kid's smartwatch) let you set a custom webhook / HTTP endpoint where the device posts its coordinates. Paste PetBond's webhook URL into the device's app or admin panel, and you'll see live location on the Tracking page. For Tractive/Fi/Petkit — we're working on direct integrations; join the waitlist above.
      </section>
    </div>
  );
}

const SUPPORTED_BRANDS_OK = ["generic"];

function WaitlistCTA({ brand, brandLabel, email, setEmail, done, onJoin }) {
  if (done) return <div className="bg-leaf/30 rounded-2xl p-4 text-sm font-bold inline-flex items-center gap-2"><Check className="w-4 h-4"/> You're on the waitlist for {brandLabel}. We'll email you when it's ready.</div>;
  return (
    <div className="bg-brand/5 border border-brand/20 rounded-2xl p-4 space-y-3" data-testid="waitlist-cta">
      <div>
        <div className="font-bold text-sm">{brandLabel} integration coming soon</div>
        <div className="text-xs text-ink-soft">We're building direct integrations with {brandLabel}. Get notified the moment it goes live — and we'll throw in 1 month of Premium free.</div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <input value={email} onChange={e => setEmail(e.target.value)} placeholder="you@email.com" className="flex-1 bg-white border border-line rounded-2xl px-3 py-2.5 font-bold text-sm outline-none focus:border-brand"/>
        <button type="button" onClick={onJoin} disabled={!email} data-testid={`waitlist-join-${brand}`} className="bg-brand text-white rounded-full px-5 py-2.5 font-bold text-sm disabled:opacity-50 hover:bg-brand-hover inline-flex items-center gap-2"><Mail className="w-4 h-4"/> Join waitlist</button>
      </div>
    </div>
  );
}
